import { debounce, getTrackingService, cdnService, setWallet, setCurrencyMultipliers, setInterpolationParams, setUiBackgroundConfig, isMobileQuery, isTabletQuery, isDesktopQuery, setMobile, setTablet, setDesktop, queryTranslations, modalService, getFeatureEnabled, getFeatureValueV2, configQuery } from '@gig/magic-common';
import { LANGUAGE_UPDATED, USER_LOGGED_IN_UPDATED, Feature } from '@gig/magic-messages';
import { CssBreakpointsV2Service } from './services/breakpoint/breakpoint.service.js';
import { magic } from '@gig/magic-sdk';
import './services/config/toolkit-config.interface.js';
import { ConfigV2Service } from './services/config/config-v2.service.js';
import { CookieService } from './services/cookie/cookie.service.js';
import { AffiliateService } from './services/affiliate/affiliate.service.js';
import './services/country/country.constants.js';
import { CountryService } from './services/country/country.service.js';
import { DeviceV2Service } from './services/device/device.service.js';
import { BalanceService } from './services/balance/balance.service.js';
import { UserPlayControlService } from './services/user/user.play-control.service.js';
import { UserSessionService } from './services/user/user-session.service.js';
import { JurisdictionService } from './services/jurisdiction/jurisdiction.service.js';
import { ExperimentService } from './services/experiment/experiment.service.js';
import { LanguageService } from './services/language/language.service.js';
import { TranslationService } from './services/translation/translation.service.js';
import { TrackingService } from './services/tracking/tracking.service.js';
import { FeatureService } from './services/features/features.service.js';
import { GamesService } from './services/games/games.service.js';
import { GamesListService } from './services/games/games-list.service.js';
import { UserRecommendedGamesService } from './services/games/user-recommended-games.service.js';
import { GameSessionService } from './services/game-session/game-session.service.js';
import { MessagingService } from './services/messaging/messaging.service.js';
import { GameplayService } from './services/gameplay/gameplay.service.js';
import { AmlService } from './services/aml/aml.service.js';
import { AccountService } from './services/account/account.service.js';
import { PepBlockService } from './services/account/pep-block.service.js';
import { JackpotsService } from './services/jackpots/jackpots.service.js';
import { NetworkConnectionStatusService } from './services/network-connection-status/network-connection-status.service.js';
import { PageNotFoundService } from './services/page-not-found/page-not-found.js';
import { TransportType } from '@gig/magic-sdk/dist/transport';
import { GamesFetchService } from './services/games/games-fetch.service.js';
import { setLoyaltyInterpolationParams, setMetaInformation, setUserInterpolationParams, initWakeLock } from './utils/toolkit.utils.js';
import { GeoLocationService } from './services/geo-location/geo-location.service.js';
import { CloudflareService } from './services/cloudflare/cloudflare.service.js';

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class Toolkit {
  constructor() {
    this.cookieService = null;
    this.countryService = null;
    this.affiliateService = null;
    this.configService = null;
    this.translationService = null;
    this.featureService = null;
    this.deviceService = null;
    this.subscriptions = [];
    this.user = null;
    this.debouncedLoyaltyPointsUpdate = debounce(this.handleLoyaltyPointsUpdate, 300);
  }
  bootstrap(config, countryToJurisdiction, defaultJurisdiction) {
    return __async(this, null, function* () {
      modalService.initialise();
      yield magic.setup({
        key: config.magic.apiToken,
        cdnUrl: config.magic.cdnUrl,
        httpUrl: config.magic.httpUrl,
        wsUrl: config.magic.wsUrl,
        transport: TransportType.BOTH,
        recordLastActivity: true,
        wsReconnect: true,
        brand: config.magic.brand
      });
      yield this.waitForSession(config);
      this.setUpConfiguration(config);
      new NetworkConnectionStatusService();
      new UserSessionService();
      this.cookieService = new CookieService();
      this.countryService = new CountryService(this.cookieService);
      this.affiliateService = new AffiliateService(this.cookieService);
      this.configService = new ConfigV2Service(config);
      this.deviceService = new DeviceV2Service();
      new LanguageService();
      new CssBreakpointsV2Service(config.cssBreakpoints, this.deviceService);
      new BalanceService();
      new ExperimentService(config.experiments);
      new GamesService();
      new GameSessionService();
      new GamesListService();
      new GameplayService();
      new JurisdictionService(this.countryService, this.configService, countryToJurisdiction, defaultJurisdiction);
      this.translationService = new TranslationService();
      this.syncMetaInformation();
      new AccountService();
      new MessagingService();
      new UserPlayControlService();
      new PepBlockService();
      new AmlService();
      this.featureService = new FeatureService(this.countryService, this.deviceService);
      new GamesFetchService();
      new JackpotsService();
      new UserRecommendedGamesService();
      new PageNotFoundService();
      yield this.setUpDeviceInfo();
      getFeatureEnabled(Feature.WAKE_LOCK).then((wakeLockEnabled) => {
        if (wakeLockEnabled) {
          initWakeLock();
        }
      });
      this.syncUserInterpolationParams();
      new GeoLocationService();
      new CloudflareService();
      getFeatureValueV2(Feature.GTM_ID).then((cmsGtmId) => {
        return configQuery("gtmContainerId").then((configGtmId) => {
          const gtmId = cmsGtmId || configGtmId;
          if (gtmId) {
            this.injectGoogleTagManager(gtmId);
          }
        });
      });
      this.initTracking();
    });
  }
  get affiliate() {
    if (!this.affiliateService) {
      throw new Error("Affiliate service is not available, did you run toolkit.bootstrap()?");
    }
    return this.affiliateService;
  }
  waitForSession(config) {
    return __async(this, null, function* () {
      if (!magic.session.getUser()) {
        return;
      }
      const currentTime = new Date().getTime();
      const lastActivityTime = magic.getLastRequestTimestamp();
      if (!lastActivityTime || currentTime - lastActivityTime > config.sessionExpireTime) {
        yield magic.methods.users.resumeLoginV2();
        return;
      }
      magic.methods.users.resumeLoginV2();
    });
  }
  injectGoogleTagManager(gtmContainerId) {
    if (!gtmContainerId)
      return;
    if (document.getElementById("gtm-script"))
      return;
    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}`;
    document.head.appendChild(script);
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      "event": "gtm.js"
    });
  }
  initTracking() {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    getTrackingService().onTrack((event) => {
      window.dataLayer.push(event);
    });
    new TrackingService(getTrackingService());
  }
  setUpConfiguration(config) {
    var _a, _b;
    magic.methods.games.setup({
      production: config.environment === "production"
    });
    cdnService.setup(config.magic.imgCdnUrl);
    setWallet({
      merchant: (_a = config.wallet) == null ? void 0 : _a.merchant,
      paymentUrl: (_b = config.wallet) == null ? void 0 : _b.paymentUrl
    });
    setCurrencyMultipliers(config.currencyMultipliers);
    setInterpolationParams(config.interpolationParams);
    setUiBackgroundConfig(config.uiBackground);
  }
  setUpDeviceInfo() {
    return __async(this, null, function* () {
      const [isMobile, isTablet, isDesktop] = yield Promise.all([
        isMobileQuery(),
        isTabletQuery(),
        isDesktopQuery()
      ]);
      setMobile(isMobile);
      setTablet(isTablet);
      setDesktop(isDesktop);
    });
  }
  handleLoyaltyPointsUpdate(data) {
    setLoyaltyInterpolationParams(data.progress, data.level, data.points);
  }
  handleUserSessionCreated() {
    return __async(this, null, function* () {
      const loyaltyPromise = magic.invokeCached(60, magic.methods.loyalty, "userProgress")({
        userId: this.user.userId
      });
      const loyaltyData = yield loyaltyPromise;
      setLoyaltyInterpolationParams(loyaltyData.progress, loyaltyData.level, loyaltyData.points);
      this.subscriptions.push(magic.events.loyaltyPointsUpdate((data) => this.debouncedLoyaltyPointsUpdate(data)).unsub);
    });
  }
  syncMetaInformation() {
    queryTranslations().then(() => {
      setMetaInformation();
    });
    window.eventBus.subscribe(LANGUAGE_UPDATED, () => {
      setMetaInformation();
    });
  }
  syncUserInterpolationParams() {
    window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, (e) => {
      const isLoggedIn = !!e.payload;
      if (isLoggedIn) {
        this.user = magic.session.getUser();
        this.handleUserSessionCreated();
      }
      if (!isLoggedIn) {
        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];
      }
      setUserInterpolationParams();
    }, { replay: true });
  }
}
const toolkit = new Toolkit();

export { toolkit };
